import { useContext, useEffect, useMemo, useRef, useState } from "react";
import AppColors from "../../../constants/AppColors";
import { NavLink, useNavigate } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { 
  Box,
  Button,
  Divider,
  HStack,
  Text,
  useBoolean
 } from "@chakra-ui/react";
import { replaceRoute, toTitleCase } from "../../../utils/common.utils";
import { FONT_FAMILY } from "../../../constants/Theme";
import { CQTreeTable, DotMenu, TextInput, usePaginationSessionStorage } from "@laxmimanogna/code-quick-components";
import { SearchIcon } from "@chakra-ui/icons";
import { AccountContext } from '../../../providers/AccountsProvider';
import { createTreeTableProps, paginationProps } from '../../../utils/cq_tree_table_props';
import { ACTIVE, INACTIVE } from "../../../constants/constants";
import ROUTES from "../../../constants/Routes";
import { DotMenuIcon } from "../../../constants/IconData";
import { DeleteModal } from "../components/DeleteModal";
import { PermanentDeleteModal } from "../components/PermanentDeleteModal";
import { AttachmentCell } from "../../assigned_audits/components/tables/table_config";


// ─── Sub-header themes ────────────────────────────────────────────────────────
const DEPT_SUBHEADER     = { bg: '#F1F5F9', color: '#64748B', };
const PROVIDER_SUBHEADER = { bg: '#F1F5F9', color: '#64748B', };
const HOSPITAL_SUBHEADER = { bg: '#F1F5F9', color: '#64748B', }

const ARRAY_SIZE_TO_MANAGE_ACCOUNT_URL_MAPPING = {
  1: ROUTES.GET_ACCOUNT,
  2: ROUTES.GET_ACCOUNT_HOSPITAL,
  3: ROUTES.GET_ACCOUNT_DEPARTMENT,
  4: ROUTES.GET_ACCOUNT_PHYSICIAN
}

const ARRAY_SIZE_TO_EDIT_ACCOUNT_URL_MAPPING = {
  1: ROUTES.EDIT_ACCOUNT,
  2: ROUTES.GET_HOSPITAL,
  3: ROUTES.GET_DEPARTMENT,
  4: ROUTES.EDIT_PHYSICIAN
}

const columnHelper = createColumnHelper();

// ─── Shared action button ─────────────────────────────────────────────────────
function ViewButton({ onClick }) {
  return (
    <Button
      size="sm"
      bg={AppColors.secondary}
      color="white"
      borderRadius="3xl"
      px={4}
      fontWeight="normal"
      fontSize="sm"
      _hover={{
        transition: 'all .1s ease',
        bgColor: AppColors.white,
        color: AppColors.secondary,
        outline: `2px solid ${AppColors.secondary}`,
      }}
      onClick={onClick}
    >
      View
    </Button>
  );
}

// ─── N/A helper ──────────────────────────────────────────────────────────────
const NA = () => <Text color={AppColors.gray} textAlign="center">N/A</Text>;

const NewAccountsTableV3 = ({PAGE_NAME, TABLE_NAME, showArchived}) => {
    const tableRef = useRef(null);
      // const dashboardContext = useContext(Dashboa);
      const navigate = useNavigate();
      const {
        getPaginationSession,
        setPaginationSession
      } = usePaginationSessionStorage();
    
      // const { treeData, isHierarchyLoading, getHierarchyTree, hospitalFilter, setHospitalFilter } = dashboardContext;
    
      const [searchText, setSearchText] = useState('');
      const [ordering, setOrdering]     = useState('');
      const [highlightedRowId, setHighlightedRowId] = useState(null);
      const [isDeleteModalOpen, idmoState] = useBoolean(false);
      const [accountId, setAccountId] = useState();
      const [accountName, setAccountName] = useState('');
      const [isPermanentDeleteModalOpen, ipdmoState] = useBoolean(false);
      const [isPermanentDeleteChecked, ipdcState] = useBoolean(false);
      const [confirmationText, setConfirmationText] = useState('');
      const [deletionPreview, setDeletionPreview] = useState(null);
      const [isChecked, icState] = useBoolean(false);
      const accountsContext = useContext(AccountContext);
      const {
        accountsList,
        getAccountsList,
        isLoadingAccountsList,
        accountFilters
      } = accountsContext;

      useEffect(() => {
        let searching = !!(accountFilters.search && accountFilters.search.trim());
        getAccountsList.loadAPI(PAGE_NAME, TABLE_NAME, searching);
        
      }, [accountFilters, getAccountsList.showOrdering]);

      useEffect(() => {
        if (ordering) {
          getAccountsList.setParams({ ordering });
        }  
      }, [ordering]);

      useEffect(() => {        
        if (accountsList.length > 0) {
          const itemId = getAccountsList.restoreNavigationState(PAGE_NAME, TABLE_NAME, true);
          if (itemId) {
            const navData = itemId.split(',').map(Number);
            
            setHighlightedRowId(navData[0]);
            tableRef.current.expandRow(navData.slice(1).join("."));            
                                
              setTimeout(() => {
                setHighlightedRowId(null);
              }, 2000);
            }
          }
        
      }, [accountsList]);
    

      const RenderActionComponent = ({row}) => {
        let status = row.original.is_active ? ACTIVE : INACTIVE;
        const [rowIds, setRowIds] = useState([]);
        const [manageAccountUrl, setManageAccountUrl] = useState(null);
        const [editAccountUrl, setEditAccountUrl] = useState(null);
        
        useState(() => {
          if (!row.original) {
            return undefined;
          }
          let curRow = row;
          let ids = [];
          let rowIds = [];
          while (true) {
            ids.push(curRow.original.id);
            rowIds.push(curRow.index);
            if (curRow.depth === 0) {
              break;
            } 
            curRow = curRow.getParentRow();
          }

          rowIds.reverse();

          setRowIds(rowIds.slice(0, -1));

          ids.reverse();
          const [
            accountId,
            hospitalId = null,
            departmentId = null,
            physicianId = null
          ] = ids;

          setManageAccountUrl(replaceRoute(ARRAY_SIZE_TO_MANAGE_ACCOUNT_URL_MAPPING[ids.length], {
            accountId,
            hospitalId,
            departmentId,
            physicianId
          }))

          setEditAccountUrl(replaceRoute(ARRAY_SIZE_TO_EDIT_ACCOUNT_URL_MAPPING[ids.length], {
            accountId,
            hospitalId,
            departmentId,
            physicianId}
          ))
        }, [row.original])

        const storeNavigationState = () => {
          const paginationSession = getPaginationSession(PAGE_NAME, TABLE_NAME);                        
          setPaginationSession(PAGE_NAME, TABLE_NAME, {...paginationSession, 
            ...('search' in accountFilters && {search: accountFilters.search}),
            ...(getAccountsList.showOrdering && {ordering: getAccountsList.showOrdering})})  
          accountsContext.getAccountsList.saveNavigationState(
            PAGE_NAME, 
            TABLE_NAME, 
            // stored data is a list. First item is the hightlighted row and the rest are rows that should be expanded
            [
              row.original.id,
              ...rowIds
            ]
          )
        }
        
        
          // Actions for archived accounts
          return (
          <>
            {showArchived ? (
              <HStack justifyContent={'center'} spacing={'sm'}>
                <NavLink
                  state={{ accountName: row.original.name }}
                  to={manageAccountUrl}
                  onClick={storeNavigationState}
                >
                  <Button
                    variant="solid"
                    borderRadius={'full'}
                    fontWeight={'bold'}
                    px={12}
                    backgroundColor={
                      status === INACTIVE ? AppColors.darkGray : AppColors.secondary
                    }
                    color={'white'}
                    disabled={!row.original.is_active}
                    outline={`2px`}
                    _hover={{
                      transition: 'all .1s ease',
                      bgColor: AppColors.white,
                      color: AppColors.secondary,
                      outline: `2px solid ${AppColors.secondary}`,
                    }}
                  >
                    Manage
                  </Button>
                </NavLink>

                {["Health System", "Physicians Group"].includes(row.original.type) && (
                  <DotMenu
                    icon={<DotMenuIcon />}
                    menuDataItem={[
                      {
                        onClick: async () => {
                          await accountsContext.restoreAccount(row.original.id);
                        },
                        children: <Box color={'green'}>Restore Account</Box>,
                      },
                      {
                        onClick: async () => {
                          setAccountId(row.original.id);
                          setAccountName(row.original.name);
                          ipdmoState.on();
                          const preview =
                            await accountsContext.getAccountDeletionPreview(
                              row.original.id
                            );
                          if (!preview) {
                            ipdmoState.off();
                            return;
                          }
                          setDeletionPreview(preview);
                        },
                        children: <Box color={'red'}>Permanent Delete</Box>,
                      },
                    ]}
                  />
                )}
              </HStack>
            ) : (
              <HStack justifyContent={'center'} spacing={'sm'}>
                <NavLink
                  state={{ accountName: row.original.name }}
                  to={manageAccountUrl}
                  onClick={storeNavigationState}
                >
                  <Button
                    variant="solid"
                    borderRadius={'full'}
                    fontWeight={'bold'}
                    px={12}
                    backgroundColor={
                      status === INACTIVE ? AppColors.darkGray : AppColors.secondary
                    }
                    color={'white'}
                    disabled={!row.original.is_active}
                    outline={`2px`}
                    _hover={{
                      transition: 'all .1s ease',
                      bgColor: AppColors.white,
                      color: AppColors.secondary,
                      outline: `2px solid ${AppColors.secondary}`,
                    }}
                  >
                    Manage
                  </Button>
                </NavLink>

                <DotMenu
                  icon={<DotMenuIcon />}
                  menuDataItem={[
                    {
                      onClick: () => {
                        storeNavigationState();
                        navigate(editAccountUrl);
                      },
                      children: <Box color={'black'}>Edit Account</Box>,
                    },
                    ...(
                      ["Health System", "Physicians Group"].includes(
                        row.original.type
                      )
                        ? [
                            {
                              onClick: () => {
                                idmoState.on();
                                setAccountId(row.original.id);
                                setAccountName(row.original.name);
                              },
                              children: (
                                <Box color={'orange'}>Archive Account</Box>
                              ),
                            },
                            {
                              onClick: async () => {
                                setAccountId(row.original.id);
                                setAccountName(row.original.name);
                                ipdmoState.on();
                                const preview =
                                  await accountsContext.getAccountDeletionPreview(
                                    row.original.id
                                  );
                                if (!preview) {
                                  ipdmoState.off();
                                  return;
                                }
                                setDeletionPreview(preview);
                              },
                              children: (
                                <Box color={'red'}>Permanent Delete</Box>
                              ),
                            },
                          ]
                        : []
                    ),
                  ]}
                />
              </HStack>
            )}
          </>
        );
    }
      // ─── Columns ─────────────────────────────────────────────────────────────
      // All three depths share the same column definitions.
      // Cells check info.row.depth to decide what to render.
      // Depth 0 = hospital, depth 1 = department, depth 2 = provider
      const columns = [
    
        // Col 1: Name
        columnHelper.accessor('name', {
          id: 'name',
          header: () => 'Accounts',
          subHeader: {
            1: { label: 'Hospital', ...HOSPITAL_SUBHEADER, sortable: true },
            2: { label: 'Department',   ...DEPT_SUBHEADER, sortable: true },
            3: { label: 'Provider',   ...PROVIDER_SUBHEADER, sortable: true },
          },
          cell: info => {
            const depth = info.row.depth;
            const val   = info.getValue();
            if (depth === 2) {
              // Provider: first_name + last_name
              const { first_name, last_name } = info.row.original;
              return <Text fontWeight={500} textAlign={"start"} fontSize="13px" color="blue.700">{val || toTitleCase(`${first_name ?? ''} ${last_name ?? ''}`.trim() )}</Text>;
            }
            if (depth === 1) return <Text textAlign={"start"} fontWeight={500} fontSize="13px" color="green.700">{toTitleCase(val)}</Text>;
            return <Text fontWeight={600} textAlign={"start"} fontSize="14px" color={AppColors.secondary}>{val}</Text>;
          },
        }),

        columnHelper.accessor('type', {
          id: 'type',
          header: () => 'Type',
          enableSorting: false,
          
          cell: info => {
            let val = info.getValue();
            const depth = info.row.depth;
            
            if (!val) {
              const depth = info.row.depth;
              if (depth === 0) {
                val = "Health System";
              } else if (depth === 1) {
                val = "Hospital";
              } else if (depth === 2) {
                val = "Department";
              } else {
                val = "Provider";
              }
            }
            if (!depth) {
              return val != null ? <Text textAlign="center" fontSize="13px">{val}</Text> : <NA />;
            }
          },
        }),

        columnHelper.accessor('state', {
          id: 'state',
          header: () => 'State',
          enableSorting: false,
          subHeader: {
            1: { label: 'State', ...HOSPITAL_SUBHEADER, sortable: true },
            // 2: { label: '',   ...DEPT_SUBHEADER, sortable: true },
            // 3: { label: '',   ...PROVIDER_SUBHEADER, sortable: true },
          },
          cell: info => {
            const val   = info.getValue();
            const depth = info.row.depth;
            if (!depth) {
              return <AttachmentCell attachments={[...new Set(info.row.original.children.map(child => child.state))]} />
            }
            if (depth === 1) {
              return <Text textAlign="center" fontSize="13px">{val}</Text>;
            }
            
          },
        }),

        columnHelper.accessor('is_active', {
          id: 'Status',
          header: () => 'Status',
          enableSorting: false,
          subHeader: {
            1: { label: 'Status', ...HOSPITAL_SUBHEADER, sortable: true },
            2: { label: 'Status',   ...DEPT_SUBHEADER, sortable: true },
            3: { label: 'Status',   ...PROVIDER_SUBHEADER, sortable: true },
          },
          cell: info => {
            const row   = info.getValue();
            const depth = info.row.depth;
            const val   = row ? "Active" : "Inactive";
            return <Text textAlign="center" fontSize="13px">{val}</Text>;
          },
        }),
      
    
        columnHelper.accessor(row => row, {
          id: "action",
          header: () => '',
          enableSorting: false,
          cell: info => {
            return <RenderActionComponent row={info.row} />
          },
        }),
    
      ];
      
      function handlePreviousPage() {
        getAccountsList.loadPrevious(PAGE_NAME, TABLE_NAME);
      }
    
      function handleNextPage() {
        getAccountsList.loadNext(PAGE_NAME, TABLE_NAME);
      }
    
      function handleSizeChange(size) {
        getAccountsList.loadSize(PAGE_NAME, TABLE_NAME, size);
      }
    
      function handlePageChangeByNumber(pageNumber) {
        getAccountsList.loadPageByNumber(PAGE_NAME, TABLE_NAME, pageNumber);
      }

      const onModalClose = () => {
        icState.off();
        idmoState.off();
      };
  
      return (
        <Box marginTop={3} width="100%" bg={AppColors.white} padding={4} borderRadius="lg" shadow="md">
          <CQTreeTable
                ref={tableRef}
                data={accountsList ?? []}
                columns={columns}
                getSubRows={(row) => row.children}
                manualPagination={true}
                manualSortBy={false}
                isLoading={isLoadingAccountsList}
                autoExpand={row => row.expand}
                {...createTreeTableProps(highlightedRowId)}
                {...paginationProps(accountsContext.getAccountsList)}
                onAsc={header => {
                setOrdering(header);
                }}
                onDesc={header => {
                setOrdering(`-${header}`);
                }}
                onPreviousPage={handlePreviousPage}
                onNextPage={handleNextPage}
                onSizeChange={handleSizeChange}
                onPageChangeByNumber={handlePageChangeByNumber}
            />
            <DeleteModal
              isDeleteLoading={accountsContext.isDepartmentLoading}
              onDelete={async () => {
                await accountsContext.deleteAccount(accountId);
                idmoState.off();
                //navigate to previous page
                navigate(ROUTES.ACCOUNTS_V2);
              }}
              isChecked={isChecked}
              icState={icState}
              isDeleteModalOpen={isDeleteModalOpen}
              closeDeleteModal={() => onModalClose()}
              name={accountName}
              condition={
                'I understand this will archive this Health System.'
              }
              isArchivedModel={true}
            />
            <PermanentDeleteModal
              isDeleteLoading={accountsContext.isPermanentDeleteLoading}
              onDelete={async () => {
                await accountsContext.permanentDeleteAccount(accountId, confirmationText);
                ipdmoState.off();
                setConfirmationText('');
                setDeletionPreview(null);
                navigate(ROUTES.ACCOUNTS_V2);
              }}
              isChecked={isPermanentDeleteChecked}
              icState={ipdcState}
              isDeleteModalOpen={isPermanentDeleteModalOpen}
              closeDeleteModal={() => {
                ipdcState.off();
                ipdmoState.off();
                setConfirmationText('');
                setDeletionPreview(null);
              }}
              name={accountName}
              confirmationText={confirmationText}
              setConfirmationText={setConfirmationText}
              deletionPreview={deletionPreview}
            />
        </Box>
      );

}

export default NewAccountsTableV3;